import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "react-router-dom"
import Header from "@/components/Header";import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { QrCode } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email({ message: "无效的邮箱地址" }),
  password: z.string().min(6, { message: "密码至少需要6个字符" }),
});
const registerSchema = z.object({
  email: z.string().email({ message: "无效的邮箱地址" }),
  password: z.string().min(6, { message: "密码至少需要6个字符" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "密码不匹配",
  path: ["confirmPassword"],
});

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "无效的邮箱地址" }),
});

const SignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRegisterSuccess, setIsRegisterSuccess] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isWeChatOpen, setIsWeChatOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const registerForm = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const forgotPasswordForm = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onLoginSubmit(values: z.infer<typeof loginSchema>) {
    setIsLoading(true);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || '登录失败');
      }

      login(data.token, data.user);
      toast.success("登录成功！");
      navigate('/');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "登录失败");
    } finally {
      setIsLoading(false);
    }
  }

  async function onRegisterSubmit(values: z.infer<typeof registerSchema>) {
    setIsLoading(true);
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          password: values.password
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '注册失败');
      }

      toast.success("账号创建成功！请检查您的邮箱以验证账号。");
      setIsRegisterSuccess(true);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "注册失败");
    } finally {
      setIsLoading(false);
    }
  }

  async function onForgotPasswordSubmit(values: z.infer<typeof forgotPasswordSchema>) {
    setIsLoading(true);
    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '请求失败');
      }

      toast.success(data.message || "如果该邮箱存在，我们已发送重置链接。");
      setIsForgotPassword(false);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "请求失败");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-grow flex items-center justify-center p-4 mt-16">
        {isRegisterSuccess ? (
          <Card className="w-[400px]">
            <CardHeader>
              <CardTitle className="text-green-600">注册成功</CardTitle>
              <CardDescription>
                验证邮件已发送，请查收。
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                我们已向 <strong>{registerForm.getValues("email")}</strong> 发送了一封验证邮件。请点击邮件中的链接激活您的账号。
              </p>
              <Button 
                className="w-full" 
                onClick={() => setIsRegisterSuccess(false)}
              >
                前往登录
              </Button>
            </CardContent>
          </Card>
        ) : isForgotPassword ? (
          <Card className="w-[400px]">
            <CardHeader>
              <CardTitle>忘记密码</CardTitle>
              <CardDescription>
                请输入您的邮箱，我们将发送重置密码的链接。
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Form {...forgotPasswordForm}>
                <form onSubmit={forgotPasswordForm.handleSubmit(onForgotPasswordSubmit)} className="space-y-4">
                  <FormField
                    control={forgotPasswordForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>邮箱</FormLabel>
                        <FormControl>
                          <Input placeholder="m@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "发送中..." : "发送重置链接"}
                  </Button>
                  <Button 
                    variant="link" 
                    className="w-full" 
                    onClick={() => setIsForgotPassword(false)}
                    type="button"
                  >
                    返回登录
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        ) : (
        <Tabs defaultValue="login" className="w-[400px]">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">登录</TabsTrigger>
            <TabsTrigger value="register">注册</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle>登录</CardTitle>
                <CardDescription>
                  请输入您的邮箱以登录您的账号。
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>邮箱</FormLabel>
                          <FormControl>
                            <Input placeholder="m@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>密码</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end">
                      <Button 
                        variant="link" 
                        className="px-0 font-normal" 
                        onClick={() => setIsForgotPassword(true)}
                        type="button"
                      >
                        忘记密码？
                      </Button>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "登录中..." : "登录"}
                    </Button>
                  </form>
                </Form>

                {/* <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      或者
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full border-green-600 text-green-600 hover:bg-green-50 hover:text-green-700"
                  type="button"
                  onClick={() => setIsWeChatOpen(true)}
                >
                  <QrCode className="mr-2 h-4 w-4" /> 微信扫码登录
                </Button> */}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle>创建账号</CardTitle>
                <CardDescription>
                  请输入您的邮箱以创建您的账号。
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Form {...registerForm}>
                  <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-4">
                    <FormField
                      control={registerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>邮箱</FormLabel>
                          <FormControl>
                            <Input placeholder="m@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>密码</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>确认密码</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "创建中..." : "创建账号"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        )}
      </main>

      {/* <Dialog open={isWeChatOpen} onOpenChange={setIsWeChatOpen}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle className="text-center">微信登录</DialogTitle>
            <DialogDescription className="text-center">
              请使用微信扫描下方二维码
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4">
            <div className="w-48 h-48 bg-gray-100 flex items-center justify-center mb-4 rounded-lg border">
              <QrCode className="h-24 w-24 text-gray-400" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              扫码后点击"确认登录"
              <br />
              <span className="text-xs text-gray-400">（此功能开发中）</span>
            </p>
          </div>
        </DialogContent>
      </Dialog> */}
      <Footer />
    </div>
  );
};

export default SignIn;
